/**
 * Clientul HTTP al dashboard-ului.
 *
 * Reguli respectate deliberat:
 *  • cookie-urile de sesiune sunt HttpOnly și merg automat (same-origin) — tokenul nu ajunge
 *    niciodată în JavaScript;
 *  • CSRF: tokenul din cookie-ul `menuvia_csrf` este trimis în header la orice mutație
 *    (double-submit), inclusiv la primul request al unei sesiuni noi;
 *  • erorile vin ca `application/problem+json` (RFC 9457) și sunt traduse într-un obiect
 *    `ApiError` cu mesaj pentru utilizator, erori pe câmpuri și cod — niciodată text tehnic;
 *  • un 401 înseamnă „sesiune expirată": se notifică aplicația, care redirecționează la login.
 */

export interface ApiErrorPayload {
  type: string;
  title: string;
  status: number;
  detail: string;
  errors?: Record<string, string[]>;
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly errors: Record<string, string[]>;
  readonly requestId?: string;

  constructor(payload: ApiErrorPayload) {
    super(payload.detail || payload.title);
    this.name = 'ApiError';
    this.status = payload.status;
    this.code = payload.type.split('/').pop() ?? 'error';
    this.errors = payload.errors ?? {};
    this.requestId = payload.requestId;
  }

  /** Mesaj sigur de afișat utilizatorului (fără detalii tehnice). */
  get userMessage(): string {
    if (this.status === 429) return 'Prea multe cereri. Reîncearcă în câteva secunde.';
    if (this.status >= 500) return 'A apărut o problemă la server. Am notat-o și revenim.';
    return this.message;
  }
}

const API_BASE = '/api/v1';
const CSRF_COOKIE = 'menuvia_csrf';

let sessionExpiredHandler: (() => void) | null = null;

/** Aplicația își înregistrează reacția la expirarea sesiunii (redirect la login). */
export function onSessionExpired(handler: () => void): void {
  sessionExpiredHandler = handler;
}

/* ---------------------------------------------------------------- transport sesiune */

const TOKEN_STORAGE_KEY = 'menuvia_session_token';

/**
 * Mediile în care cookie-urile nu pot fi stocate deloc (iframe cu `sandbox` fără `allow-same-origin`,
 * browsere care blochează cookie-urile terță parte, moduri private stricte) ar face aplicația
 * inutilizabilă: autentificarea ar reuși, dar nicio cerere ulterioară nu ar mai fi autentificată.
 *
 * Soluția este un al doilea transport, standard pentru SPA-uri și API-uri: tokenul de sesiune
 * trimis în `Authorization: Bearer`. Cookie-ul rămâne calea implicită (HttpOnly, protejat de XSS),
 * iar tokenul se folosește doar când cookie-urile chiar nu sunt disponibile.
 */
let memoryToken: string | null = null;
let storageAvailable: boolean | null = null;

function canUseStorage(): boolean {
  if (storageAvailable !== null) return storageAvailable;
  try {
    const probe = '__menuvia_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    storageAvailable = true;
  } catch {
    // Origine opacă sau stocare blocată: rămâne tokenul în memorie, valabil cât trăiește fila.
    storageAvailable = false;
  }
  return storageAvailable;
}

export function readStoredToken(): string | null {
  if (memoryToken) return memoryToken;
  if (!canUseStorage()) return null;
  try {
    memoryToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    memoryToken = null;
  }
  return memoryToken;
}

export function storeToken(token: string | null): void {
  memoryToken = token;
  if (!canUseStorage()) return;
  try {
    if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignorăm: tokenul rămâne în memorie */
  }
}

/**
 * Contextul de rulare poate păstra cookie-uri, dar nu le poate trimite (iframe încorporat pe alt
 * domeniu cu protecții de tracking, cookie-uri terță parte blocate, `SameSite` care blochează
 * cererea). Acolo autentificarea reușește, dar sesiunea nu se păstrează — iar clientul nu are cum
 * să afle asta din `document.cookie`, care rămâne citibil.
 *
 * Serverul ne spune în `meta.cookieTransport` dacă a primit cookie-urile noastre. Când răspunsul
 * este „nu", trecem pentru restul sesiunii pe transportul cu token în antet, singurul care
 * funcționează fără cookie-uri. Fără acest comutator, autentificarea ar părea că „nu face nimic".
 */
let bearerPreferred = false;

/** Comută sesiunea pe transportul cu token în antet (ireversibil în cadrul sesiunii curente). */
export function preferBearerTransport(): void {
  bearerPreferred = true;
}

export function isBearerTransportPreferred(): boolean {
  return bearerPreferred;
}

export function readCookie(name: string): string | null {
  try {
    // `document.cookie` ARUNCĂ într-un iframe sandbox fără `allow-same-origin` (previzualizări,
    // embed-uri). Fără acest try/catch, orice cerere către API eșua cu o excepție de securitate
    // care nu are legătură cu serverul.
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

async function ensureCsrfToken(): Promise<string | null> {
  const existing = readCookie(CSRF_COOKIE);
  if (existing) return existing;
  // Prima încărcare: cookie-ul de CSRF este emis de server la orice citire publică.
  // `try/catch` obligatoriu: dacă respectiva cerere e blocată (rețea, CORS, context încadrat),
  // nu trebuie să aruncăm — apelantul comută pe transportul prin antet.
  try {
    await fetch(`${API_BASE}/meta/public-config`, { credentials: 'same-origin' });
  } catch {
    return null;
  }
  return readCookie(CSRF_COOKIE);
}

/** true când contextul curent nu poate păstra cookie-uri (deci e nevoie de token în header). */
export function tokenTransportRequired(): boolean {
  if (readStoredToken()) return true;
  return readCookie(CSRF_COOKIE) === null;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Rutele publice (contact, consent) nu trimit CSRF și nu declanșează redirect la 401. */
  anonymous?: boolean;
  /**
   * Cere explicit transportul cu token în antet, fără să atașeze vreun token existent.
   * Folosit la reluarea autentificării într-un mediu care nu păstrează cookie-uri.
   */
  forceBearer?: boolean;
  /**
   * Trimite serverului lista cookie-urilor pe care le vedem în browser, ca el să poată spune dacă
   * le primește. Folosit doar la verificarea sesiunii — vezi `preferBearerTransport`.
   */
  declareCookies?: boolean;
}

/** Cookie-urile aplicației pe care clientul le poate vedea (CSRF-ul este citibil intenționat). */
function visibleCookieNames(): string {
  return ['menuvia_csrf', 'menuvia_session'].filter((name) => readCookie(name)).join(',');
}

/**
 * Antetele de autentificare, într-un singur loc.
 *
 * Sunt folosite de toate cererile — inclusive de descărcarea fișierelor — pentru că sesiunea are
 * două transporturi (cookie + CSRF sau token în antet) și niciun apel nu trebuie să „știe" care e
 * activ. Orice loc care ar construi antetele separat ar putea rămâne în urmă la prima schimbare.
 */
async function authHeaders(method: string, options: RequestOptions): Promise<Record<string, string>> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.declareCookies) headers['x-cookies-visible'] = visibleCookieNames();

  const forceBearer = options.forceBearer === true;
  const bearer = forceBearer ? null : readStoredToken();
  if (bearer) {
    headers.authorization = `Bearer ${bearer}`;
    // Cerem serverului să ne dea tokenul în corp la autentificare (nu putem folosi cookie-uri).
    headers['x-session-transport'] = 'bearer';
  } else if (forceBearer || (bearerPreferred && !options.anonymous)) {
    // Reluare explicită sau context despre care serverul ne-a spus că nu primește cookie-urile:
    // cerem tokenul de sesiune în corp și renunțăm la acreditările ambientale (deci nici la CSRF,
    // care protejează exact cererile bazate pe cookie-uri).
    headers['x-session-transport'] = 'bearer';
  } else if (method !== 'GET') {
    const token = await ensureCsrfToken();
    if (token) {
      headers['x-csrf-token'] = token;
    } else {
      // Cookie-ul de CSRF nu există nici după încercarea de emitere ⇒ contextul nu poate păstra
      // cookie-uri (origine opacă în iframe sandbox, cookie-uri terță parte blocate). Trecem pe
      // transportul prin antet: serverul știe astfel că cererea nu se bazează pe acreditări
      // ambientale și nu cere token CSRF, iar la autentificare ne întoarce tokenul de sesiune.
      headers['x-session-transport'] = 'bearer';
    }
  }
  return headers;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = await authHeaders(method, options);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    // Eșec de rețea sau cerere blocată de browser: mesaj clar, nu „Failed to fetch".
    if ((cause as Error)?.name === 'AbortError') throw cause;
    throw new ApiError({
      type: 'https://docs.menuvia.ro/errors/network',
      title: 'Conexiune întreruptă',
      status: 0,
      detail: 'Nu am putut contacta serverul. Verifică conexiunea la internet și reîncearcă.',
    });
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const problem = (payload ?? {}) as Partial<ApiErrorPayload>;
    const error = new ApiError({
      type: problem.type ?? `https://docs.menuvia.ro/errors/http-${response.status}`,
      title: problem.title ?? 'Eroare',
      status: response.status,
      detail: problem.detail ?? 'Cererea nu a putut fi finalizată.',
      ...(problem.errors ? { errors: problem.errors } : {}),
      ...(problem.requestId ? { requestId: problem.requestId } : {}),
    });

    if (response.status === 401 && !options.anonymous) {
      storeToken(null);
      sessionExpiredHandler?.();
    }
    throw error;
  }

  // Cu `raw: true` întoarcem răspunsul întreg (date + meta); altfel, doar câmpul `data`.
  const data = (options as { raw?: boolean }).raw
    ? (payload as T)
    : ((payload as { data?: T })?.data ?? (payload as T));

  // Punct central: dacă serverul a returnat un token de sesiune (login/înregistrare în medii fără
  // cookie-uri), îl păstrăm imediat. Altfel ar depinde de fiecare apelant să-l salveze — exact
  // genul de detaliu care se uită și lasă utilizatorul cu o sesiune care „nu ține".
  const maybeToken = (data as { sessionToken?: unknown } | undefined)?.sessionToken;
  if (typeof maybeToken === 'string' && maybeToken.length > 0) storeToken(maybeToken);

  return data;
}

/**
 * Cerere care întoarce răspunsul brut (`{ data, meta }`), folosită când avem nevoie și de metadate.
 * Reutilizează exact același transport, deci sesiunea și CSRF-ul se comportă identic.
 */
async function requestRaw<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
  return request<T>(path, { ...options, raw: true } as RequestOptions & { raw: true });
}

/**
 * Descărcarea unui fișier (export CSV, rapoarte) prin sesiunea curentă.
 *
 * Nu se folosește un link direct: endpoint-urile de export verifică permisiuni și apartenența la
 * cont, deci trebuie să treacă prin același transport autentificat ca restul aplicației — altfel
 * datele contului ar deveni accesibile oricui are adresa.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const headers = await authHeaders('GET', {});
  const response = await fetch(`${API_BASE}${path}`, { headers, credentials: 'same-origin' });
  if (!response.ok) {
    let detail = 'Fișierul nu a putut fi descărcat.';
    try {
      const payload = (await response.json()) as { detail?: string };
      if (payload.detail) detail = payload.detail;
    } catch {
      /* răspuns fără corp JSON: păstrăm mesajul generic */
    }
    throw new ApiError({
      type: 'https://docs.menuvia.ro/errors/download-failed',
      title: 'Descărcare eșuată',
      status: response.status,
      detail,
    });
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = match?.[1] ?? fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Eliberăm memoria imediat ce browserul a pornit descărcarea.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Încărcarea unui fișier: corpul este chiar imaginea (nu JSON), iar serverul verifică tipul
 * din conținut. Păstrăm același tratament de erori (problem+json) ca la restul cererilor.
 */
export async function uploadFile<T>(path: string, file: File, options: { signal?: AbortSignal } = {}): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    'content-type': file.type || 'application/octet-stream',
  };

  const bearer = readStoredToken();
  if (bearer) {
    headers.authorization = `Bearer ${bearer}`;
    headers['x-session-transport'] = 'bearer';
  } else {
    const token = await ensureCsrfToken();
    if (token) headers['x-csrf-token'] = token;
    else headers['x-session-transport'] = 'bearer';
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: file,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    throw new ApiError({
      type: 'https://docs.menuvia.ro/errors/network',
      title: 'Conexiune întreruptă',
      status: 0,
      detail: 'Nu am putut trimite fișierul. Verifică conexiunea și reîncearcă.',
    });
  }

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;
  if (!response.ok) {
    const problem = (payload ?? {}) as Partial<ApiErrorPayload>;
    throw new ApiError({
      type: problem.type ?? 'https://docs.menuvia.ro/errors/internal-error',
      title: problem.title ?? 'Încărcarea a eșuat',
      status: response.status,
      detail: problem.detail ?? 'Fișierul nu a putut fi încărcat.',
      errors: problem.errors,
      requestId: problem.requestId,
    });
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, options),
  /**
   * Ca `get`, dar întoarce răspunsul complet (inclusiv `meta`). Folosit acolo unde serverul trimite
   * date și metadate în același răspuns — de exemplu lista de coduri QR cu sumarul contului.
   */
  getRaw: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => requestRaw<T>(path, options),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { method: 'POST', body, ...options }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
  /** Descarcă un fișier generat de server (CSV, PDF), cu sesiunea curentă. */
  download: downloadFile,
};
